import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useAdminAIHistory,
  formatDuration,
  formatTokens,
  getStatusColor,
  formatHistoryDate,
  type AIGenerationHistoryItem,
  type GenerationStatus,
} from "../../hooks/useAdminAIHistory";
import type { DisciplineSlug } from "@jumpbean/shared";

/**
 * AIHistoryPage - Admin page to view AI content generation history.
 *
 * Features:
 * - List of all AI generation runs with timestamp, admin, discipline, count, target_skills
 * - Status indicators (success, partial, failed)
 * - Link to challenges created from each generation run
 * - Filtering by discipline, status, date range
 * - Aggregate stats (total generated, approval rate, skill coverage)
 * - Token usage and duration info if available
 * - Export functionality
 */
export function AIHistoryPage() {
  const {
    history,
    isLoading,
    error,
    filters,
    pagination,
    updateFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    getStats,
    refetch,
  } = useAdminAIHistory();

  const stats = getStats();

  // Export functionality
  const handleExport = (format: "csv" | "json") => {
    const data = history.map((item) => ({
      id: item.id,
      discipline: item.discipline,
      level: item.level,
      challenge_type: item.challenge_type,
      count: item.count,
      target_skills: item.target_skills?.join(", ") || "",
      model_used: item.model_used,
      status: item.status,
      challenges_generated: item.challenges_generated,
      challenges_approved: item.challenges_approved,
      tokens_input: item.tokens_input || 0,
      tokens_output: item.tokens_output || 0,
      duration_ms: item.duration_ms || 0,
      created_at: item.created_at,
      completed_at: item.completed_at || "",
    }));

    let content: string;
    let mimeType: string;
    let extension: string;

    if (format === "csv") {
      const headers = Object.keys(data[0] || {}).join(",");
      const rows = data.map((row) =>
        Object.values(row)
          .map((val) => `"${String(val).replace(/"/g, '""')}"`)
          .join(",")
      );
      content = [headers, ...rows].join("\n");
      mimeType = "text/csv";
      extension = "csv";
    } else {
      content = JSON.stringify(data, null, 2);
      mimeType = "application/json";
      extension = "json";
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-generation-history.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Generation History</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track and analyze AI content generation runs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport("csv")}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={history.length === 0}
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={history.length === 0}
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Runs"
          value={stats.totalGenerations}
          icon={<RunsIcon />}
          color="magic"
        />
        <StatCard
          label="Challenges Generated"
          value={stats.totalChallengesGenerated}
          icon={<ChallengesIcon />}
          color="blue"
        />
        <StatCard
          label="Challenges Approved"
          value={stats.totalChallengesApproved}
          icon={<ApprovedIcon />}
          color="green"
        />
        <StatCard
          label="Approval Rate"
          value={`${stats.approvalRate}%`}
          icon={<RateIcon />}
          color={stats.approvalRate < 50 ? "red" : stats.approvalRate < 80 ? "yellow" : "green"}
        />
      </div>

      {/* Discipline distribution */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Generation by Discipline (this page)
          </h3>
          <div className="flex flex-wrap gap-3">
            {(["math", "spelling", "patterns", "typing", "programming"] as DisciplineSlug[]).map(
              (discipline) => {
                const count = stats.byDiscipline[discipline];
                if (count === 0) return null;
                return (
                  <DisciplineBadge key={discipline} discipline={discipline} count={count} />
                );
              }
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Discipline
            </label>
            <select
              value={filters.discipline || ""}
              onChange={(e) =>
                updateFilters({
                  discipline: e.target.value as DisciplineSlug | undefined || undefined,
                })
              }
              className="block w-40 rounded-lg border-gray-300 text-sm focus:border-magic-500 focus:ring-magic-500"
            >
              <option value="">All Disciplines</option>
              <option value="math">Math</option>
              <option value="spelling">Spelling</option>
              <option value="patterns">Patterns</option>
              <option value="typing">Typing</option>
              <option value="programming">Programming</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Status
            </label>
            <select
              value={filters.status || ""}
              onChange={(e) =>
                updateFilters({
                  status: e.target.value as GenerationStatus | undefined || undefined,
                })
              }
              className="block w-32 rounded-lg border-gray-300 text-sm focus:border-magic-500 focus:ring-magic-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="partial">Partial</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {(filters.discipline || filters.status) && (
            <button
              onClick={clearFilters}
              className="mt-5 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear filters
            </button>
          )}

          <button
            onClick={refetch}
            className="mt-5 ml-auto px-3 py-1.5 text-sm font-medium text-magic-600 hover:text-magic-700 hover:bg-magic-50 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* History table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-magic-500" />
            <p className="mt-2 text-sm text-gray-500">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              <HistoryIcon />
            </div>
            <p className="text-gray-500">No generation history found.</p>
            {(filters.discipline || filters.status) && (
              <button
                onClick={clearFilters}
                className="mt-2 text-sm text-magic-600 hover:text-magic-700"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discipline
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target Skills
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Generated
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approved
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {history.map((item) => (
                    <HistoryRow key={item.id} item={item} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                {pagination.total} results
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={prevPage}
                  disabled={pagination.page === 1}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={nextPage}
                  disabled={!pagination.hasMore}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ----- Components -----

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: "magic" | "green" | "blue" | "red" | "yellow";
}

function StatCard({ label, value, icon, color }: StatCardProps) {
  const colorClasses = {
    magic: "bg-magic-50 text-magic-600",
    green: "bg-green-50 text-green-600",
    blue: "bg-blue-50 text-blue-600",
    red: "bg-red-50 text-red-600",
    yellow: "bg-amber-50 text-amber-600",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}
        >
          <div className="w-5 h-5">{icon}</div>
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

interface DisciplineBadgeProps {
  discipline: DisciplineSlug;
  count: number;
}

const DISCIPLINE_COLORS: Record<DisciplineSlug, { bg: string; text: string }> = {
  math: { bg: "bg-magic-100", text: "text-magic-700" },
  spelling: { bg: "bg-pink-100", text: "text-pink-700" },
  patterns: { bg: "bg-purple-100", text: "text-purple-700" },
  typing: { bg: "bg-blue-100", text: "text-blue-700" },
  programming: { bg: "bg-green-100", text: "text-green-700" },
};

const DISCIPLINE_LABELS: Record<DisciplineSlug, string> = {
  math: "Math",
  spelling: "Spelling",
  patterns: "Patterns",
  typing: "Typing",
  programming: "Programming",
};

function DisciplineBadge({ discipline, count }: DisciplineBadgeProps) {
  const colors = DISCIPLINE_COLORS[discipline];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}
    >
      {DISCIPLINE_LABELS[discipline]}
      <span className="opacity-75">({count})</span>
    </span>
  );
}

interface HistoryRowProps {
  item: AIGenerationHistoryItem;
}

function HistoryRow({ item }: HistoryRowProps) {
  const statusColors = getStatusColor(item.status);
  const disciplineColors = DISCIPLINE_COLORS[item.discipline];
  const totalTokens = (item.tokens_input || 0) + (item.tokens_output || 0);

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="text-sm text-gray-900">{formatHistoryDate(item.created_at)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${disciplineColors.bg} ${disciplineColors.text}`}
        >
          {DISCIPLINE_LABELS[item.discipline]}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="text-sm text-gray-700 font-mono">{item.challenge_type}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <span className="text-sm text-gray-900">{item.level}</span>
      </td>
      <td className="px-4 py-3">
        {item.target_skills && item.target_skills.length > 0 ? (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {item.target_skills.slice(0, 2).map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
              >
                {skill}
              </span>
            ))}
            {item.target_skills.length > 2 && (
              <span className="text-xs text-gray-500">
                +{item.target_skills.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap">
        <span className="text-sm text-gray-500">{item.count}</span>
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap">
        <span className="text-sm font-medium text-gray-900">{item.challenges_generated}</span>
      </td>
      <td className="px-4 py-3 text-center whitespace-nowrap">
        <span
          className={`text-sm font-medium ${
            item.challenges_approved > 0 ? "text-green-600" : "text-gray-400"
          }`}
        >
          {item.challenges_approved}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors.bg} ${statusColors.text}`}
        >
          {item.status}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <span className="text-sm text-gray-500">{formatDuration(item.duration_ms)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <span className="text-sm text-gray-500">{formatTokens(totalTokens)}</span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-center">
        {item.challenges_generated > 0 ? (
          <Link
            to={`/admin/challenges?generationId=${item.id}`}
            className="text-sm text-magic-600 hover:text-magic-700 font-medium"
          >
            View
          </Link>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>
    </tr>
  );
}

// ----- Icons -----

function RunsIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChallengesIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ApprovedIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RateIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-full h-full">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default AIHistoryPage;
